/**
 * Parcels Table Component
 * Displays paginated list of parcel geolocation operations
 */

import type { AdminParcelSummary, ParcelsResponse } from '@tender-monitor/shared/types/admin';
import { formatTimestamp } from '../../utils/formatTime';
import { Pagination } from './Pagination';

type ParcelsTableProps = {
  data: ParcelsResponse;
  loading: boolean;
  onPageChange: (page: number) => void;
  onRowClick: (item: AdminParcelSummary) => void;
};

function StatusBadge({ status }: { status: string }) {
  return <span className={`status-badge status-badge--${status}`}>{status}</span>;
}

function ResultCell({ item }: { item: AdminParcelSummary }) {
  if (item.geolocationStatus === 'success') {
    if (item.areaM2) {
      return <span style={{ color: '#276749' }}>{item.areaM2.toLocaleString('pl-PL')} m²</span>;
    }
    return <span style={{ color: '#276749' }}>Zlokalizowano</span>;
  }
  if (item.geolocationStatus === 'not_found') {
    return <span style={{ color: '#d69e2e' }}>Brak w ULDK</span>;
  }
  if (item.geolocationStatus === 'failed' && item.geolocationError) {
    return (
      <span style={{ color: '#c53030' }} title={item.geolocationError}>
        {item.geolocationError.substring(0, 40)}
        {item.geolocationError.length > 40 ? '...' : ''}
      </span>
    );
  }
  if (item.geolocationStatus === 'pending') {
    return <span style={{ color: '#718096' }}>Oczekuje</span>;
  }
  return <span style={{ color: '#718096' }}>—</span>;
}

function Timestamp({ value }: { value: string | null }) {
  const { relative, absolute } = formatTimestamp(value);
  return (
    <span className="timestamp" title={absolute}>
      {relative}
    </span>
  );
}

export function ParcelsTable({ data, loading, onPageChange, onRowClick }: ParcelsTableProps) {
  if (data.items.length === 0) {
    return (
      <div className="empty-state">
        <p>Brak wyników dla wybranych filtrów</p>
      </div>
    );
  }

  return (
    <div>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Status</th>
            <th>Źródło</th>
            <th>Działka</th>
            <th>Obręb / Gmina</th>
            <th>Wynik</th>
            <th>Utworzono</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((item) => (
            <tr
              key={item.id}
              className="clickable"
              onClick={() => onRowClick(item)}
              style={{ opacity: loading ? 0.5 : 1 }}
            >
              <td>
                <StatusBadge status={item.geolocationStatus} />
              </td>
              <td>{item.sourceName}</td>
              <td>{item.parcelNumber}</td>
              <td>
                {item.district || '-'} / {item.commune || '-'}
              </td>
              <td>
                <ResultCell item={item} />
              </td>
              <td>
                <Timestamp value={item.createdAt} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Pagination pagination={data.pagination} onPageChange={onPageChange} />
    </div>
  );
}
